import React from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Icon,
  Badge,
  useColorMode,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FiUsers,
  FiAward,
  FiClock,
  FiBriefcase,
  FiGlobe,
  FiShield,
  FiArrowRight,
  FiCheck,
} from "react-icons/fi";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { brandConfig, getColor } from "../brandConfig";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const softwareBadges = [
    "Smokeball",
    "LEAP",
    "Clio",
    "Actionstep",
    "InfoTrack",
  ];

  const forLawFirms = [
    "Access trained, certified legal professionals",
    "Workers proficient in your legal software",
    "Flexible staffing - part-time or full-time",
    "6-minute increment time tracking built in",
    "Transparent billing and reporting",
  ];

  const forProfessionals = [
    "Showcase your certifications and skills",
    "Video introduction profiles",
    "Track your hours with precision",
    "Connect with international law firms",
    "Build your professional reputation",
  ];

  return (
    <Box minH="100vh" bg={getColor("background.main", colorMode)}>
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        pt={{ base: 20, md: 32 }}
        pb={{ base: 16, md: 24 }}
        bg={colorMode === "light"
          ? "linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)"
          : "linear-gradient(135deg, #0B1929 0%, #1E3A5F 100%)"}
      >
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center" color="white">
            <Badge
              colorScheme="blue"
              px={4}
              py={1}
              borderRadius="full"
              fontSize="sm"
              bg="rgba(255, 255, 255, 0.15)"
              color="white"
            >
              Legal Staffing Platform
            </Badge>
            <Heading
              as="h1"
              size="3xl"
              fontWeight="800"
              lineHeight="1.1"
              maxW="800px"
            >
              {brandConfig.siteName}
            </Heading>
            <Text
              fontSize="xl"
              maxW="600px"
              opacity={0.9}
              fontWeight="500"
            >
              {brandConfig.tagline}
            </Text>
            <Text
              fontSize="md"
              maxW="700px"
              opacity={0.7}
            >
              Connecting trained Chinese legal professionals with overseas law firms.
              Comprehensive staffing with worker profiles, certifications, and time tracking.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg="white"
                color="#1E3A5F"
                fontWeight="700"
                px={8}
                _hover={{
                  bg: "gray.100",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.2s"
                onClick={() => navigate("/login")}
                rightIcon={<FiArrowRight />}
              >
                Get Started
              </Button>
              <Button
                size="lg"
                variant="outline"
                color="white"
                borderColor="rgba(255, 255, 255, 0.4)"
                fontWeight="600"
                px={8}
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                  borderColor: "white",
                  transform: "translateY(-2px)",
                }}
                transition="all 0.2s"
                onClick={() => navigate("/login")}
              >
                Sign In
              </Button>
            </HStack>

            {/* Software Badges */}
            <HStack spacing={3} pt={8} flexWrap="wrap" justify="center">
              <Text fontSize="sm" opacity={0.6} mr={2}>
                Software Certified:
              </Text>
              {softwareBadges.map((badge) => (
                <Badge
                  key={badge}
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="rgba(255, 255, 255, 0.12)"
                  color="white"
                  fontSize="xs"
                  fontWeight="600"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                >
                  {badge}
                </Badge>
              ))}
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Value Props Section */}
      <Box py={{ base: 16, md: 24 }} bg={getColor("background.light", colorMode)}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
            {/* For Law Firms */}
            <Box
              p={8}
              borderRadius="2xl"
              bg={getColor("background.card", colorMode)}
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
            >
              <HStack mb={6}>
                <Icon as={FiBriefcase} boxSize={8} color="#1E3A5F" />
                <Heading size="lg" color={getColor("text.primary", colorMode)}>
                  For Law Firms
                </Heading>
              </HStack>
              <VStack align="start" spacing={3}>
                {forLawFirms.map((item) => (
                  <HStack key={item} spacing={3}>
                    <Icon as={FiCheck} color="#22C55E" flexShrink={0} />
                    <Text color={getColor("text.secondary", colorMode)}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>

            {/* For Legal Professionals */}
            <Box
              p={8}
              borderRadius="2xl"
              bg={getColor("background.card", colorMode)}
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
            >
              <HStack mb={6}>
                <Icon as={FiUsers} boxSize={8} color="#4A90D9" />
                <Heading size="lg" color={getColor("text.primary", colorMode)}>
                  For Legal Professionals
                </Heading>
              </HStack>
              <VStack align="start" spacing={3}>
                {forProfessionals.map((item) => (
                  <HStack key={item} spacing={3}>
                    <Icon as={FiCheck} color="#22C55E" flexShrink={0} />
                    <Text color={getColor("text.secondary", colorMode)}>{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Box py={{ base: 16, md: 24 }} bg={getColor("background.main", colorMode)}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={getColor("text.primary", colorMode)}>
              Platform Features
            </Heading>
            <Text fontSize="lg" color={getColor("text.secondary", colorMode)} maxW="600px">
              Everything you need to manage legal staffing efficiently
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={8}>
            {[
              {
                icon: FiUsers,
                title: "Worker Profiles",
                desc: "Comprehensive profiles with CVs, video intros, skills, and certifications",
              },
              {
                icon: FiAward,
                title: "Certifications",
                desc: "Track software proficiency across Smokeball, LEAP, Clio, and more",
              },
              {
                icon: FiClock,
                title: "Time Tracking",
                desc: "6-minute increment time entries per matter, industry standard",
              },
              {
                icon: FiBriefcase,
                title: "Client Management",
                desc: "Full CRM for managing law firm relationships",
              },
              {
                icon: FiGlobe,
                title: "Global Reach",
                desc: "Connect professionals in China with firms worldwide",
              },
              {
                icon: FiShield,
                title: "Billing & Invoicing",
                desc: "Generate and send professional invoices with Stripe integration",
              },
            ].map((feature) => (
              <Box
                key={feature.title}
                p={6}
                borderRadius="xl"
                bg={colorMode === "light"
                  ? "white"
                  : "rgba(30, 58, 95, 0.2)"}
                border="1px solid"
                borderColor={getColor("border.light", colorMode)}
                _hover={{
                  transform: "translateY(-4px)",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
                }}
                transition="all 0.3s"
              >
                <Icon
                  as={feature.icon}
                  boxSize={10}
                  color="#4A90D9"
                  mb={4}
                />
                <Heading size="md" mb={2} color={getColor("text.primary", colorMode)}>
                  {feature.title}
                </Heading>
                <Text fontSize="sm" color={getColor("text.secondary", colorMode)}>
                  {feature.desc}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        py={{ base: 16, md: 20 }}
        bg="linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)"
      >
        <Container maxW="container.md">
          <VStack spacing={6} textAlign="center" color="white">
            <Heading size="xl">Ready to get started?</Heading>
            <Text fontSize="lg" opacity={0.9}>
              Join LinkOSx and connect with legal talent today.
            </Text>
            <Button
              size="lg"
              bg="white"
              color="#1E3A5F"
              fontWeight="700"
              px={10}
              _hover={{
                bg: "gray.100",
                transform: "translateY(-2px)",
              }}
              transition="all 0.2s"
              onClick={() => navigate("/login")}
              rightIcon={<FiArrowRight />}
            >
              Create Account
            </Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default Home;
